// Language-neutral content shared by the Persian and English vouchers. Each language maps
// a row's kind to its own label, so the two vouchers always show the same lines.

interface RoomAmounts {
  payablePrice: number
  discount?: number
  additionalDiscount?: number
  promotionDiscount?: number
  cancelationPenalty?: number
  changePenalty?: number
  crsStatus?: string
  pmsStatus?: string
}

export type PriceRowKind =
  | 'listPrice'
  | 'rateDiscount'
  | 'additionalDiscount'
  | 'promotion'
  | 'changePenalty'
  | 'cancelPenalty'
  | 'total'

export interface PriceRow {
  kind: PriceRowKind
  amount: number
  sign: '' | '-' | '+'
  tone?: 'discount' | 'penalty'
  isTotal?: boolean
}

export const isCancelledStatus = (status?: string) =>
  status?.toLowerCase() === 'cancelled'

// The status shown on the voucher: the hotel's PMS status once it has one, otherwise the
// CRS status, so a booking not yet in the PMS still shows where it stands.
export const bookingStatus = (basic: {
  pmsStatus?: string
  crsStatus?: string
}) => basic.pmsStatus || basic.crsStatus || ''

export const bookerName = (booker: {
  fullName?: string
  firstName?: string
  lastName?: string
}) =>
  booker.fullName ||
  [booker.firstName, booker.lastName].filter(Boolean).join(' ')

// The room's price as a statement that reads top to bottom, following how the CRS
// builds the amount (app/ValueObjects/Booking/Amount.php, BookingManager cancellation):
// - an active room pays afterTax + changePenalty, where afterTax is net of the rate,
//   operator (additional) and promotion discounts, kept as separate amounts;
// - a cancelled room's payable is replaced by the applied cancellation penalty, so the
//   original price and discounts no longer describe what is owed.
// Discount and penalty lines only appear when non-zero.
export function priceRows(room: RoomAmounts): PriceRow[] {
  const cancelPenalty = Math.max(0, room.cancelationPenalty ?? 0)
  const changePenalty = Math.max(0, room.changePenalty ?? 0)
  const total: PriceRow = {
    kind: 'total',
    amount: room.payablePrice,
    sign: '',
    isTotal: true
  }
  // An applied cancellation penalty means the CRS bills the penalty, not the stay
  // (Amount::create: payable = penalty whenever it is non-zero), so it counts as cancelled
  // even before the status catches up.
  const cancelled =
    isCancelledStatus(room.pmsStatus) ||
    isCancelledStatus(room.crsStatus) ||
    cancelPenalty > 0
  if (cancelled) {
    // The penalty is the charge itself here, and what is owed; the total is derived from
    // it so the two lines cannot disagree.
    const owed: PriceRow = { ...total, amount: cancelPenalty }
    return cancelPenalty > 0
      ? [
          {
            kind: 'cancelPenalty',
            amount: cancelPenalty,
            sign: '',
            tone: 'penalty'
          },
          owed
        ]
      : [owed]
  }

  // Only positive discounts get a line, and the list price is built from exactly those
  // lines, so the statement reconciles to the payable amount by construction.
  const rateDiscount = Math.max(0, room.discount ?? 0)
  const additionalDiscount = Math.max(0, room.additionalDiscount ?? 0)
  const promotion = Math.max(0, room.promotionDiscount ?? 0)
  const listPrice =
    room.payablePrice -
    changePenalty +
    rateDiscount +
    additionalDiscount +
    promotion
  const rows: PriceRow[] = [{ kind: 'listPrice', amount: listPrice, sign: '' }]
  const add = (row: PriceRow) => {
    if (row.amount > 0) rows.push(row)
  }
  add({
    kind: 'rateDiscount',
    amount: rateDiscount,
    sign: '-',
    tone: 'discount'
  })
  add({
    kind: 'additionalDiscount',
    amount: additionalDiscount,
    sign: '-',
    tone: 'discount'
  })
  add({ kind: 'promotion', amount: promotion, sign: '-', tone: 'discount' })
  add({
    kind: 'changePenalty',
    amount: changePenalty,
    sign: '+',
    tone: 'penalty'
  })
  return [...rows, total]
}

// One entry per visual line. A "\n" inside a single react-pdf Text drops the first letter
// of every Persian line and leaves a broken glyph at its end, so callers must render each
// line as its own Text. Policies arrive as plain text with "\n" or "***" separators, or
// as <p>/<br> markup.
export function ratePolicyLines(text: string): string[] {
  return (
    text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      // [^<>] keeps this linear: [^>] backtracks quadratically on many unclosed "<".
      .replace(/<[^<>]*>/g, '')
      .split(/\*\*\*|\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
  )
}
